import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert as BsAlert, Button, Badge } from 'react-bootstrap';
import { notificationApi } from '../services/notificationApi';
import { getStoredDeviceId } from '../utils/serviceWorker';
import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import { useUnresolvedAlerts, useAllAlerts, useResolveAlert, useClaimAlert, useReleaseAlert } from '../hooks/useAlerts';
import {
  BUCKET,
  BUCKET_TITLE,
  BUCKET_COLOR,
  arabicPlural,
  groupByAge,
  formatPropertyLabel,
  formatPlatformLabel
} from '../utils/alertDisplay';
import { formatDate } from '../utils/dateFormatter';

// Safety cap against detector-runaway flooding the DOM. Prod is at 0 alerts
// today; 100 is generous headroom before we need virtualization/pagination.
const RENDER_CAP = 100;

const BOOKING_CONFLICT_TYPE = 'booking_conflict';

// ⚠️ An alert's `message` is FROZEN at raise time by _findOrCreateLockAlert (deliberate — the text
// must not churn on every re-detection). For a battery that is now actively misleading: the alert is
// raised at 30% and re-detected as it falls, so the card would read «وصلت إلى 30%» while the lock is
// at 12%. `meta.batteryLevel` IS refreshed on every re-detection, so it is the live figure and the
// one an operator must act on. Shown alongside the message rather than replacing it — the raise-time
// reading is genuine history and says how fast the battery is dropping.
const LOW_BATTERY_TYPE = 'lock.low_battery';

const getPriorityColor = (priority) => {
  if (priority?.colorCode) return priority.colorCode;
  switch (priority?.name) {
    case 'critical': return '#dc3545';
    case 'high': return '#fd7e14';
    case 'medium': return '#ffc107';
    case 'low': return '#6c757d';
    default: return '#6c757d';
  }
};

const bookingLabel = (booking) => {
  if (booking.confirmationCode) return `#${booking.confirmationCode}`;
  return `#${String(booking._id || '').slice(-6).toUpperCase()}`;
};

// Accepts relatedBookings in either populated form (objects with
// _id/confirmationCode/sourcePlatform) OR raw ObjectId strings (older alerts
// created before the populate was added). Always returns object shape.
const normalizeRelatedBooking = (entry) => {
  if (entry && typeof entry === 'object') {
    return {
      _id: entry._id,
      confirmationCode: entry.confirmationCode || null,
      sourcePlatform: entry.sourcePlatform || null
    };
  }
  return { _id: entry, confirmationCode: null, sourcePlatform: null };
};

// wa.me needs bare digits; tel: keeps the + so the dialer treats it as E.164.
const phoneDigits = (phone) => String(phone || '').replace(/[^\d]/g, '');

const AlertRow = ({ alert, bucketKey, onResolve, isResolving, currentUserId, onClaim, onRelease, isClaimBusy, isFocused, focusRef }) => {
  const borderColor = BUCKET_COLOR[bucketKey] || '#999';
  const priorityColor = getPriorityColor(alert.alertPriority);
  const propertyLabel = formatPropertyLabel(alert.property);
  const platformLabel = formatPlatformLabel(alert.platform);
  const isConflict = alert.alertType?.name === BOOKING_CONFLICT_TYPE;
  const isLowBattery = alert.alertType?.name === LOW_BATTERY_TYPE;
  const liveBatteryLevel = typeof alert.meta?.batteryLevel === 'number' ? alert.meta.batteryLevel : null;

  // Ownership (Phase 2 plumbing): assignedTo is populated { _id, name } or null.
  const ownerId = alert.assignedTo?._id || alert.assignedTo || null;
  const isClaimed = !!ownerId;
  const isMine = isClaimed && String(ownerId) === String(currentUserId);

  // relatedBookings[] populated by the P7 conflict detector. Backend projects
  // { _id, confirmationCode, sourcePlatform } per booking. Fall back to single
  // booking field for alerts created by legacy code paths.
  const conflictBookings = isConflict
    ? (alert.relatedBookings?.length
        ? alert.relatedBookings.map(normalizeRelatedBooking)
        : [alert.booking || alert.bookingId].filter(Boolean).map(normalizeRelatedBooking))
    : [];

  const dateRange = (alert.startDate && alert.endDate)
    ? `${formatDate(alert.startDate)} → ${formatDate(alert.endDate)}`
    : null;

  return (
    <div
      ref={isFocused ? focusRef : undefined}
      className={`alert-row mb-3 p-3${isFocused ? ' alert-row-focused' : ''}`}
      style={{
        background: '#fff',
        borderRadius: '6px',
        borderRight: `4px solid ${borderColor}`,
        boxShadow: isFocused ? `0 0 0 2px ${borderColor}` : '0 1px 2px rgba(0,0,0,0.06)'
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div style={{ flex: 1 }}>
          <div className="d-flex align-items-center mb-1" style={{ gap: '8px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: priorityColor,
                flexShrink: 0
              }}
              title={alert.alertPriority?.description || alert.alertPriority?.name || ''}
            />
            <strong style={{ fontSize: '15px' }}>{alert.title || 'تنبيه'}</strong>
          </div>

          {alert.property?._id ? (
            <div className="mb-1" style={{ fontSize: '13px' }}>
              <i className="bi bi-building me-1"></i>
              <Link to={`/properties/${alert.property._id}`}>{propertyLabel}</Link>
            </div>
          ) : (
            <div className="mb-1" style={{ fontSize: '13px', color: '#666' }}>
              <i className="bi bi-building me-1"></i>{propertyLabel}
            </div>
          )}

          {alert.message && (
            <div className="mb-2" style={{ fontSize: '13px', color: '#333', whiteSpace: 'pre-line' }}>
              {alert.message}
            </div>
          )}

          {isLowBattery && liveBatteryLevel !== null && (
            <div className="mb-2" style={{ fontSize: '13px' }}>
              <strong>القراءة الحالية: </strong>
              <span style={{ color: liveBatteryLevel <= 20 ? '#dc3545' : '#cc8800', fontWeight: 600 }}>
                {liveBatteryLevel}%
              </span>
            </div>
          )}

          {isConflict && conflictBookings.length > 0 && (
            <div className="mb-2" style={{ fontSize: '13px' }}>
              <strong>الحجوزات المتعارضة: </strong>
              {conflictBookings.map((booking, idx) => {
                const platformText = formatPlatformLabel(booking.sourcePlatform);
                return (
                  <React.Fragment key={booking._id}>
                    {idx > 0 && <span>، </span>}
                    <Link to={`/bookings/${booking._id}`} style={{ fontFamily: 'monospace' }}>
                      {bookingLabel(booking)}
                    </Link>
                    {platformText && (
                      <span style={{ color: '#666' }}> ({platformText})</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* Focused-card quick actions (push deep link, tasks/lock-incident-push-channel.md §4.5):
              tap-to-call / tap-to-WhatsApp the guest from existing booking data. Rendered ONLY on
              the focused card — the notification promises "one more tap", this is that tap. The
              push payload itself never carries these details; they live behind auth, here. */}
          {isFocused && !alert.resolved && alert.booking?.guestPhone && (
            <div className="mb-2 d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
              <Button
                variant="primary"
                size="sm"
                href={`tel:${alert.booking.guestPhone}`}
              >
                <i className="bi bi-telephone-fill me-1"></i>
                اتصال بالضيف{alert.booking.guestName ? ` (${alert.booking.guestName})` : ''}
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                href={`https://wa.me/${phoneDigits(alert.booking.guestPhone)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-whatsapp me-1"></i>
                واتساب الضيف
              </Button>
              {alert.booking.confirmationCode && (
                <Link to={`/bookings/${alert.booking._id}`} style={{ fontSize: '13px' }}>
                  #{alert.booking.confirmationCode}
                </Link>
              )}
            </div>
          )}

          <div style={{ fontSize: '12px', color: '#666' }}>
            {dateRange && <span><i className="bi bi-calendar3 me-1"></i>{dateRange}</span>}
            {/* For booking_conflict, per-booking (Platform) tags above already
                cover the platform info — the compound platform string is
                redundant here. For other alert types, keep it. */}
            {!isConflict && platformLabel && (
              <span className="me-3 ms-3"><i className="bi bi-globe me-1"></i>{platformLabel}</span>
            )}
            <span><i className="bi bi-clock me-1"></i>{alert.ageLabel}</span>
          </div>
        </div>

        <div style={{ flexShrink: 0, marginRight: '12px', textAlign: 'left' }}>
          {alert.resolved ? (
            <Badge bg="success" className="p-2">
              <i className="bi bi-check-circle-fill me-1"></i>
              تم الحل
            </Badge>
          ) : (
            <div className="d-flex flex-column align-items-stretch" style={{ gap: '6px' }}>
              {/* Ownership row: unclaimed → claim button; claimed → owner badge
                  (+release for the owner). Claiming ≠ resolving — it tells the
                  team "I'm on it" and stops the chase sweep re-pinging. */}
              {!isClaimed && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onClaim(alert._id)}
                  disabled={isClaimBusy}
                >
                  {isClaimBusy ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    <>
                      <i className="bi bi-person-check me-1"></i>
                      أتولى هذا
                    </>
                  )}
                </Button>
              )}
              {isClaimed && (
                <Badge bg={isMine ? 'primary' : 'secondary'} className="p-2" style={{ fontWeight: 'normal' }}>
                  <i className="bi bi-person-fill me-1"></i>
                  {isMine ? 'أنت تتابع هذا' : `يتابعه: ${alert.assignedTo?.name || 'عضو الفريق'}`}
                </Badge>
              )}
              {isMine && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onRelease(alert._id)}
                  disabled={isClaimBusy}
                >
                  <i className="bi bi-person-dash me-1"></i>
                  إلغاء الاستلام
                </Button>
              )}
              <Button
                variant="success"
                size="sm"
                onClick={() => onResolve(alert._id)}
                disabled={isResolving}
              >
                {isResolving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    جارٍ...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    حل
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BucketSection = ({ bucketKey, alerts, onResolve, resolvingId, currentUserId, onClaim, onRelease, claimBusyId, focusId, focusRef }) => {
  if (!alerts || alerts.length === 0) return null;

  const title = BUCKET_TITLE[bucketKey];
  const color = BUCKET_COLOR[bucketKey];
  const countLabel = arabicPlural(alerts.length, 'تنبيه', 'تنبيهان', 'تنبيهات');

  return (
    <section className="mb-4">
      <h5 style={{ color, borderBottom: `2px solid ${color}`, paddingBottom: '6px', marginBottom: '16px' }}>
        {title} <span style={{ color: '#666', fontSize: '14px' }}>({alerts.length} {countLabel})</span>
      </h5>
      {alerts.map(alert => (
        <AlertRow
          key={alert._id}
          alert={alert}
          bucketKey={bucketKey}
          onResolve={onResolve}
          isResolving={resolvingId === alert._id}
          currentUserId={currentUserId}
          onClaim={onClaim}
          onRelease={onRelease}
          isClaimBusy={claimBusyId === alert._id}
          isFocused={focusId != null && String(alert._id) === focusId}
          focusRef={focusRef}
        />
      ))}
    </section>
  );
};

const AlertsManagement = () => {
  const [activeNavItem, setActiveNavItem] = useState('alerts');
  const [filter, setFilter] = useState('unresolved'); // 'unresolved' | 'all'
  const [resolvingId, setResolvingId] = useState(null);
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [claimError, setClaimError] = useState(null);

  const { user } = useContext(AuthContext);
  const currentUserId = user?.userId || user?._id || user?.id || null;

  const unresolvedQuery = useUnresolvedAlerts();
  const allQuery = useAllAlerts();
  const active = filter === 'unresolved' ? unresolvedQuery : allQuery;
  const { alerts, isLoading, isError, error, refetch } = active;

  const resolveMutation = useResolveAlert();
  const claimMutation = useClaimAlert();
  const releaseMutation = useReleaseAlert();

  const handleResolve = (alertId) => {
    setResolvingId(alertId);
    resolveMutation.mutate(alertId, {
      onSettled: () => setResolvingId(null)
    });
  };

  const handleClaim = (alertId) => {
    setClaimBusyId(alertId);
    setClaimError(null);
    claimMutation.mutate(alertId, {
      onError: (err) => {
        // 409 = a teammate claimed first — the onSettled invalidation refreshes
        // the list so the real owner badge appears.
        setClaimError(err?.response?.data?.message || 'تعذّر استلام التنبيه');
      },
      onSettled: () => setClaimBusyId(null)
    });
  };

  const handleRelease = (alertId) => {
    setClaimBusyId(alertId);
    setClaimError(null);
    releaseMutation.mutate(alertId, {
      onError: (err) => {
        setClaimError(err?.response?.data?.message || 'تعذّر إلغاء استلام التنبيه');
      },
      onSettled: () => setClaimBusyId(null)
    });
  };

  const cappedAlerts = useMemo(() => alerts.slice(0, RENDER_CAP), [alerts]);
  const overflowCount = Math.max(0, alerts.length - RENDER_CAP);

  const buckets = useMemo(() => groupByAge(cappedAlerts), [cappedAlerts]);

  // ── Push deep link (tasks/lock-incident-push-channel.md §4.4) ──────────────
  // `/#/alerts?focus=<alertId>&n=1` — `focus` selects and highlights the card;
  // `n=1` is the ONLY thing that makes this count as a push-driven open (D88,
  // InboxPage precedent — never inferred from an ordinary visit).
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const cameFromPush = searchParams.get('n') === '1';
  const focusRef = useRef(null);
  const handledFocusRef = useRef(null);
  const navigationKey = searchParams.toString();
  const [focusMissing, setFocusMissing] = useState(false);

  useEffect(() => {
    if (!focusId || isLoading || isError) return;
    if (handledFocusRef.current === navigationKey) return;

    const found = cappedAlerts.some((a) => String(a._id) === focusId);

    // The alert may have resolved between the push and the tap — widen to «الكل»
    // once instead of showing a confusing empty highlight.
    if (!found && filter === 'unresolved') {
      setFilter('all');
      return;
    }

    handledFocusRef.current = navigationKey;
    setFocusMissing(!found);

    // Recorded only once the card is genuinely visible — openedAt must mean
    // "they saw it". Fire-and-forget: measurement must never break navigation.
    if (found && cameFromPush) {
      notificationApi
        .recordOpen({ subjectKind: 'alert', subjectId: focusId, deviceId: getStoredDeviceId() })
        .catch((err) => console.error('Failed to record push-driven open:', err));
    }

    // SPEND THE PUSH MARKER so a refresh cannot claim a second notification as
    // opened (the exact D88 self-corruption InboxPage documents). `focus` is
    // kept — a reload should still land on the same card.
    if (cameFromPush) {
      const spent = new URLSearchParams(searchParams);
      spent.delete('n');
      spent.delete('t');
      setSearchParams(spent, { replace: true });
    }

    if (found) {
      requestAnimationFrame(() => {
        focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [focusId, cameFromPush, cappedAlerts, isLoading, isError, filter, navigationKey, searchParams, setSearchParams]);

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="التنبيهات"
    >
      <Container fluid className="py-4" dir="rtl">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '12px' }}>
          <h3 style={{ color: '#471396', margin: 0 }}>
            <i className="bi bi-bell me-2"></i>
            إدارة التنبيهات
          </h3>

          <div className="btn-group" role="group" aria-label="تصفية">
            <Button
              variant={filter === 'unresolved' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setFilter('unresolved')}
            >
              غير محلول
            </Button>
            <Button
              variant={filter === 'all' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              الكل
            </Button>
          </div>
        </div>

        {resolveMutation.isError && (
          <BsAlert variant="danger" onClose={() => resolveMutation.reset()} dismissible>
            <i className="bi bi-exclamation-triangle me-2"></i>
            تعذّر حل التنبيه. يُرجى المحاولة مرة أخرى.
          </BsAlert>
        )}

        {claimError && (
          <BsAlert variant="warning" onClose={() => setClaimError(null)} dismissible>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {claimError}
          </BsAlert>
        )}

        {focusMissing && (
          <BsAlert variant="info" onClose={() => setFocusMissing(false)} dismissible>
            <i className="bi bi-info-circle me-2"></i>
            التنبيه المرتبط بالإشعار لم يعد موجوداً — ربما تم حله بالفعل.
          </BsAlert>
        )}

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </Spinner>
            <div className="mt-2" style={{ color: '#666' }}>جاري تحميل التنبيهات...</div>
          </div>
        )}

        {isError && !isLoading && (
          <BsAlert variant="danger" className="text-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            حدث خطأ أثناء تحميل التنبيهات
            {error?.message && <div style={{ fontSize: '12px', color: '#666' }}>{error.message}</div>}
            <div className="mt-2">
              <Button variant="outline-danger" size="sm" onClick={() => refetch()}>
                إعادة المحاولة
              </Button>
            </div>
          </BsAlert>
        )}

        {!isLoading && !isError && alerts.length === 0 && (
          <div className="text-center py-5" style={{ color: '#666' }}>
            <i className="bi bi-inbox" style={{ fontSize: '48px', color: '#ccc' }}></i>
            <div className="mt-3" style={{ fontSize: '16px' }}>
              {filter === 'unresolved' ? 'لا توجد تنبيهات غير محلولة' : 'لا توجد تنبيهات'}
            </div>
          </div>
        )}

        {!isLoading && !isError && alerts.length > 0 && (
          <>
            <BucketSection
              bucketKey={BUCKET.TODAY}
              alerts={buckets[BUCKET.TODAY]}
              onResolve={handleResolve}
              resolvingId={resolvingId}
              currentUserId={currentUserId}
              onClaim={handleClaim}
              onRelease={handleRelease}
              claimBusyId={claimBusyId}
              focusId={focusId}
              focusRef={focusRef}
            />
            <BucketSection
              bucketKey={BUCKET.THIS_WEEK}
              alerts={buckets[BUCKET.THIS_WEEK]}
              onResolve={handleResolve}
              resolvingId={resolvingId}
              currentUserId={currentUserId}
              onClaim={handleClaim}
              onRelease={handleRelease}
              claimBusyId={claimBusyId}
              focusId={focusId}
              focusRef={focusRef}
            />
            <BucketSection
              bucketKey={BUCKET.OLD}
              alerts={buckets[BUCKET.OLD]}
              onResolve={handleResolve}
              resolvingId={resolvingId}
              currentUserId={currentUserId}
              onClaim={handleClaim}
              onRelease={handleRelease}
              claimBusyId={claimBusyId}
              focusId={focusId}
              focusRef={focusRef}
            />

            {overflowCount > 0 && (
              <div className="text-center py-3" style={{ color: '#999', fontSize: '13px' }}>
                يتم عرض أول {RENDER_CAP} تنبيه. يوجد {overflowCount} تنبيه إضافي غير معروض.
              </div>
            )}
          </>
        )}
      </Container>
    </DesktopLayout>
  );
};

export default AlertsManagement;
